import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  Card as MuiCard,
  Button,
  IconButton,
  ButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  InputBase,
  Tooltip,
  TableSortLabel,
  Menu,
  Badge,
  Popover,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { RootState } from "../../redux/store";
import useStyles from "../../containers/Auth/Onboarding/style";
import appLogo from "../../assets/logo.png";
import backIcon from "../../assets/icons/backIcon.svg";
import { Search, Add, FileCopy, Pause, PlayArrow, Cancel, Delete, Send, MoreVert, Visibility, VpnKey, Security, FilterList, Edit, Lock } from "@material-ui/icons";
import { getProgramById } from "../../redux/actions/programAction";
import moment from "moment";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:9090";

interface Invitation {
  _id: string;
  invitationCode: string;
  invitationType: "public" | "private";
  programId: string;
  communityIdentifier: string;
  role: string;
  channelIds?: string[];
  email?: string;
  phoneNumber?: string;
  inviteeName?: string;
  status: string;
  sentCount?: number;
  usageCount?: number;
  pauseMessage?: string;
  expiresAt: string;
  expiryDays?: number;
  createdAt: string;
  createdBy?: { name: string; lastName: string; email: string };
  sentHistory?: Array<{
    sentAt: string;
    sentBy: string;
    method: string;
  }>;
}

const invertColor = (hex: string) => {
  let col = (hex || "#ffffff").replace(/^#/, "");
  if (col.length === 3) col = col.split("").map(c => c + c).join("");
  const num = parseInt(col || "ffffff", 16);
  const r = 255 - ((num >> 16) & 0xff);
  const g = 255 - ((num >> 8) & 0xff);
  const b = 255 - (num & 0xff);
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
};

export default function AdminInvitations() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const params = new URLSearchParams(location.search);
  const initialCommunity = params.get("community") || undefined;

  const [selectedCommunity, setSelectedCommunity] = useState<string | undefined>(initialCommunity);
  const [currentTab, setCurrentTab] = useState(0);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selected, setSelected] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [roleFilter, setRoleFilter] = useState<string>("all");
  const [bulkResendDialog, setBulkResendDialog] = useState(false);
  const [bulkResending, setBulkResending] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [editingInvitation, setEditingInvitation] = useState<Invitation | null>(null);
  const [editRole, setEditRole] = useState("member");
  const [editExpiryDays, setEditExpiryDays] = useState(7);
  const [updating, setUpdating] = useState(false);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(50);

  // Sorting states
  const [sortBy, setSortBy] = useState<"email" | "sentAt" | "status" | "role">("sentAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  // Dialog states
  const [createPublicDialog, setCreatePublicDialog] = useState(false);
  const [createPrivateDialog, setCreatePrivateDialog] = useState(false);
  const [pauseDialog, setPauseDialog] = useState(false);
  const [selectedInvitation, setSelectedInvitation] = useState<Invitation | null>(null);
  const [pauseMessage, setPauseMessage] = useState("");
  const [creating, setCreating] = useState(false);
  const [cloneSource, setCloneSource] = useState<Invitation | null>(null);
  const [creationSuccess, setCreationSuccess] = useState<{ type: 'public' | 'private'; code?: string } | null>(null);

  // Menu state for mobile actions
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; inv: Invitation } | null>(null);

  // Channel access popover state
  const [channelPopover, setChannelPopover] = useState<{
    el: HTMLElement;
    invitationId: string;
    data: any | null;
    loading: boolean;
  } | null>(null);

  // Create invitation form
  const [expiryDays, setExpiryDays] = useState(7);
  const [role, setRole] = useState("member");
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [selectedChannelIds, setSelectedChannelIds] = useState<string[]>([]);
  const [channelSearchQuery, setChannelSearchQuery] = useState("");

  const { singleProgram } = useSelector((s: RootState) => s.programReducer);
  const { user } = useSelector((s: RootState) => s.auth);

  const activeCommunity = singleProgram?.communities.find(
    (cm) => selectedCommunity && cm.communityIdentifier === selectedCommunity
  );

  const communityTheme = {
    mainColor: activeCommunity?.communityStyle?.mainColor || "#1976d2",
    secondaryColor: activeCommunity?.communityStyle?.secondaryColor || "#ffffff",
    fontStyle: activeCommunity?.communityStyle?.fontStyle || "Arial",
  };
  const fgOnSecondary = invertColor(communityTheme.secondaryColor);

  // Fetch program on mount
  useEffect(() => {
    const activeProgramEnrollment = JSON.parse(localStorage.getItem("activeProgramEnrollment") || "{}");
    const programId = activeProgramEnrollment?.programId;

    if (programId && !singleProgram) {
      dispatch(getProgramById(programId) as any);
    }
  }, [dispatch, singleProgram]);

  // Set default community
  useEffect(() => {
    if (singleProgram && !selectedCommunity) {
      const fallback = singleProgram.communities?.[0]?.communityIdentifier;
      if (fallback) {
        setSelectedCommunity(fallback);
        const searchParams = new URLSearchParams(location.search);
        searchParams.set('community', fallback);
        history.replace({ search: searchParams.toString() });
      }
    }
  }, [singleProgram, selectedCommunity, history, location.search]);

  // Fetch invitations
  const fetchInvitations = async () => {
    if (!selectedCommunity || !singleProgram?._id) return;

    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(
        `${API_BASE}/v1/invitations/${singleProgram._id}/community/${selectedCommunity}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!res.ok) throw new Error("Failed to fetch invitations");
      const data = await res.json();
      setInvitations(data.data || []);
    } catch (error) {
      console.error("Error fetching invitations:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvitations();
  }, [selectedCommunity, singleProgram?._id]);

  // Filter invitations by type
  const publicInvitations = useMemo(
    () => invitations.filter((inv) => inv.invitationType === "public"),
    [invitations]
  );

  const privateInvitations = useMemo(
    () => invitations.filter((inv) => inv.invitationType === "private"),
    [invitations]
  );

  const displayedInvitations = currentTab === 0 ? publicInvitations : privateInvitations;

  // Search and filter logic
  const filteredInvitations = useMemo(() => {
    let filtered = displayedInvitations;

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((inv) =>
        inv.invitationCode.toLowerCase().includes(query) ||
        inv.email?.toLowerCase().includes(query) ||
        inv.inviteeName?.toLowerCase().includes(query) ||
        inv.status.toLowerCase().includes(query)
      );
    }

    // Apply status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter((inv) => inv.status === statusFilter);
    }

    // Apply role filter
    if (roleFilter !== "all") {
      filtered = filtered.filter((inv) => inv.role === roleFilter);
    }

    return filtered;
  }, [displayedInvitations, searchQuery, statusFilter, roleFilter]);

  // Sorting logic
  const sortedInvitations = useMemo(() => {
    if (currentTab !== 1) return filteredInvitations; // Only sort private invitations

    const sorted = [...filteredInvitations].sort((a, b) => {
      let aVal: any;
      let bVal: any;

      switch (sortBy) {
        case "email":
          aVal = a.email || "";
          bVal = b.email || "";
          break;
        case "sentAt":
          aVal = a.sentHistory?.[a.sentHistory.length - 1]?.sentAt || a.createdAt;
          bVal = b.sentHistory?.[b.sentHistory.length - 1]?.sentAt || b.createdAt;
          break;
        case "status":
          aVal = a.status;
          bVal = b.status;
          break;
        case "role":
          aVal = a.role;
          bVal = b.role;
          break;
        default:
          return 0;
      }

      if (sortBy === "sentAt") {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
      }

      if (aVal < bVal) return sortOrder === "asc" ? -1 : 1;
      if (aVal > bVal) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [filteredInvitations, sortBy, sortOrder, currentTab]);

  // Pagination logic
  const paginatedInvitations = useMemo(() => {
    if (currentTab !== 1) return sortedInvitations; // Only paginate private invitations
    const start = currentPage * pageSize;
    const end = start + pageSize;
    return sortedInvitations.slice(start, end);
  }, [sortedInvitations, currentPage, pageSize, currentTab]);

  const totalPages = Math.ceil((currentTab === 1 ? sortedInvitations.length : sortedInvitations.length) / pageSize);

  // Reset pagination and filters when tab changes
  useEffect(() => {
    setCurrentPage(0);
    setStatusFilter("all");
    setRoleFilter("all");
    setSelected([]);
  }, [currentTab]);

  // Handle sort
  const handleSort = (field: "email" | "sentAt" | "status" | "role") => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
  };

  // Handle clone public invitation
  const handleClone = (invitation: Invitation) => {
    setCloneSource(invitation);
    setRole(invitation.role);
    setExpiryDays(invitation.expiryDays || 7);
    setSelectedChannelIds(invitation.channelIds || []);
    setCreatePublicDialog(true);
  };

  // Handle create public invitation
  const handleCreatePublic = async () => {
    if (!selectedCommunity || !singleProgram?._id) return;

    setCreating(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE}/v1/invitations`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          invitationType: "public",
          programId: singleProgram._id,
          communityIdentifier: selectedCommunity,
          role,
          expiryDays,
          channelIds: selectedChannelIds,
        }),
      });

      if (!res.ok) throw new Error("Failed to create invitation");

      const result = await res.json();
      setCreationSuccess({ type: 'public', code: result.data?.invitationCode });
      fetchInvitations();
    } catch (error) {
      console.error("Error creating public invitation:", error);
      alert("Failed to create invitation");
    } finally {
      setCreating(false);
    }
  };

  // Handle create private invitation
  const handleCreatePrivate = async () => {
    if (!selectedCommunity || !singleProgram?._id || !email) return;

    setCreating(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE}/v1/invitations`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          invitationType: "private",
          programId: singleProgram._id,
          communityIdentifier: selectedCommunity,
          role,
          email,
          firstName: firstName || undefined,
          lastName: lastName || undefined,
          phoneNumber: phoneNumber || undefined,
          expiryDays,
          channelIds: selectedChannelIds,
        }),
      });

      if (!res.ok) throw new Error("Failed to create invitation");

      const result = await res.json();
      setCreationSuccess({ type: 'private' });
      fetchInvitations();
    } catch (error) {
      console.error("Error creating private invitation:", error);
      alert("Failed to create invitation");
    } finally {
      setCreating(false);
    }
  };

  // Handle pause public invitation
  const handlePause = async () => {
    if (!selectedInvitation) return;

    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE}/v1/invitations/${selectedInvitation._id}/pause`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ pauseMessage }),
      });

      if (!res.ok) throw new Error("Failed to pause invitation");

      setPauseDialog(false);
      setPauseMessage("");
      fetchInvitations();
    } catch (error) {
      console.error("Error pausing invitation:", error);
      alert("Failed to pause invitation");
    }
  };

  // Handle resume public invitation
  const handleResume = async (invitation: Invitation) => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE}/v1/invitations/${invitation._id}/resume`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error("Failed to resume invitation");
      fetchInvitations();
    } catch (error) {
      console.error("Error resuming invitation:", error);
      alert("Failed to resume invitation");
    }
  };

  // Handle cancel public invitation
  const handleCancel = async (invitation: Invitation) => {
    if (!window.confirm(`Cancel invitation ${invitation.invitationCode}? This cannot be undone.`)) return;

    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE}/v1/invitations/${invitation._id}/cancel`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error("Failed to cancel invitation");
      fetchInvitations();
    } catch (error) {
      console.error("Error cancelling invitation:", error);
      alert("Failed to cancel invitation");
    }
  };

  // Handle resend private invitation
  const handleResend = async (invitation: Invitation) => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE}/v1/invitations/${invitation._id}/resend`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error("Failed to resend invitation");
      fetchInvitations();
      alert(`Invitation resent to ${invitation.email}`);
    } catch (error) {
      console.error("Error resending invitation:", error);
      alert("Failed to resend invitation");
    }
  };

  // Handle revoke private invitation
  const handleRevoke = async (invitation: Invitation) => {
    if (!window.confirm(`Revoke invitation for ${invitation.email}? This cannot be undone.`)) return;

    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE}/v1/invitations/${invitation._id}/revoke`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error("Failed to revoke invitation");
      fetchInvitations();
    } catch (error) {
      console.error("Error revoking invitation:", error);
      alert("Failed to revoke invitation");
    }
  };

  // Handle select all
  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const pendingIds = paginatedInvitations
        .filter((inv) => inv.status === "pending")
        .map((inv) => inv._id);
      setSelected(pendingIds);
    } else {
      setSelected([]);
    }
  };

  // Handle select one
  const handleSelectOne = (id: string) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  // Handle bulk resend
  const handleBulkResend = async () => {
    setBulkResending(true);
    try {
      const token = localStorage.getItem("token");
      const promises = selected.map((id) =>
        fetch(`${API_BASE}/v1/invitations/${id}/resend`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        })
      );

      await Promise.all(promises);
      alert(`Successfully resent ${selected.length} invitation(s)!`);
      setSelected([]);
      setBulkResendDialog(false);
      fetchInvitations();
    } catch (error) {
      console.error("Error bulk resending invitations:", error);
      alert("Some invitations failed to resend. Please try again.");
    } finally {
      setBulkResending(false);
    }
  };

  // Handle edit invitation
  const handleEditInvitation = (invitation: Invitation) => {
    setEditingInvitation(invitation);
    setEditRole(invitation.role);
    setEditExpiryDays(invitation.expiryDays || 7);
    setEditDialog(true);
  };

  // Handle update invitation
  const handleUpdateInvitation = async () => {
    if (!editingInvitation) return;

    setUpdating(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE}/v1/invitations/${editingInvitation._id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          role: editRole,
          expiryDays: editExpiryDays,
        }),
      });

      if (!res.ok) throw new Error("Failed to update invitation");

      alert("Invitation updated successfully!");
      setEditDialog(false);
      setEditingInvitation(null);
      fetchInvitations();
    } catch (error) {
      console.error("Error updating invitation:", error);
      alert("Failed to update invitation");
    } finally {
      setUpdating(false);
    }
  };

  // Copy invitation code to clipboard
  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    alert(`Invitation code "${code}" copied to clipboard!`);
  };

  // Get status chip color
  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return { bg: "#4caf50", color: "#fff" };
      case "paused":
        return { bg: "#ff9800", color: "#fff" };
      case "pending":
        return { bg: "#2196f3", color: "#fff" };
      case "accepted":
        return { bg: "#00bcd4", color: "#fff" };
      case "expired":
      case "cancelled":
      case "revoked":
        return { bg: "#f44336", color: "#fff" };
      default:
        return { bg: "#9e9e9e", color: "#fff" };
    }
  };

  // Get role display with icons
  const getRoleDisplay = (role: string) => {
    switch (role) {
      case "moderator":
        return (
          <Box display="flex" alignItems="center">
            <Security style={{ fontSize: '1em', marginRight: 4 }} />
            <span>Moderator</span>
          </Box>
        );
      case "CommunityAdmin":
        return (
          <Box display="flex" alignItems="center">
            <VpnKey style={{ fontSize: '1em', marginRight: 4 }} />
            <span>Admin</span>
          </Box>
        );
      case "member":
        return (
          <Box display="flex" alignItems="center">
            <Visibility style={{ fontSize: '1em', marginRight: 4 }} />
            <span>Member</span>
          </Box>
        );
      default:
        return role;
    }
  };

  // Fetch channel access information
  const fetchChannelAccess = async (invitationId: string) => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE}/v1/invitations/${invitationId}/channel-access`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error("Failed to fetch channel access");
      const result = await res.json();
      return result.data;
    } catch (error) {
      console.error("Error fetching channel access:", error);
      return null;
    }
  };

  // Handle channel badge click/hover
  const handleChannelBadgeClick = async (event: React.MouseEvent<HTMLElement>, invitationId: string) => {
    setChannelPopover({
      el: event.currentTarget,
      invitationId,
      data: null,
      loading: true,
    });

    const data = await fetchChannelAccess(invitationId);
    setChannelPopover((prev) =>
      prev && prev.invitationId === invitationId
        ? { ...prev, data, loading: false }
        : null
    );
  };

  // Get filtered channels for channel selection
  const getFilteredChannels = useMemo(() => {
    const channels = activeCommunity?.channels || [];
    if (!channelSearchQuery) return channels;

    const query = channelSearchQuery.toLowerCase();
    return channels.filter((ch: any) =>
      ch.channelName?.toLowerCase().includes(query) ||
      ch.channelIdentifier?.toLowerCase().includes(query)
    );
  }, [activeCommunity?.channels, channelSearchQuery]);

  // Toggle channel selection
  const handleToggleChannel = (channelId: string) => {
    setSelectedChannelIds((prev) =>
      prev.includes(channelId)
        ? prev.filter((id) => id !== channelId)
        : [...prev, channelId]
    );
  };

  if (!selectedCommunity || !activeCommunity || !singleProgram) {
    return (
      <Box p={4} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div
      className={classes.container}
      style={{
        backgroundColor: communityTheme.secondaryColor,
        minHeight: "100vh",
        padding: "20px",
        display: "flex",
        justifyContent: "center",
      }}
    >
      {/* Create Public Invitation Dialog */}
      <Dialog
        open={createPublicDialog}
        onClose={() => {
          if (!creating) {
            setCreatePublicDialog(false);
            setCloneSource(null);
          }
        }}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          style: {
            backgroundColor: communityTheme.secondaryColor,
            color: fgOnSecondary,
            fontFamily: communityTheme.fontStyle,
          },
        }}
      >
        <DialogTitle style={{ color: fgOnSecondary }}>
          {creationSuccess?.type === 'public'
            ? "✅ Invitation Created Successfully!"
            : cloneSource
              ? `Clone Public Invitation (from ${cloneSource.invitationCode})`
              : "Create Public Invitation"}
        </DialogTitle>
        <DialogContent>
          {creationSuccess?.type === 'public' ? (
            <Box py={2}>
              <Typography variant="body1" style={{ color: fgOnSecondary, marginBottom: 16 }}>
                Public invitation created successfully!
              </Typography>
              {creationSuccess.code && (
                <Box mb={2}>
                  <Typography variant="body2" style={{ color: `${fgOnSecondary}99`, marginBottom: 8 }}>
                    Invitation Code:
                  </Typography>
                  <Box display="flex" alignItems="center">
                    <code style={{
                      backgroundColor: `${communityTheme.mainColor}22`,
                      color: communityTheme.mainColor,
                      padding: '8px 12px',
                      borderRadius: '4px',
                      fontSize: '1.1em',
                      fontWeight: 'bold',
                      marginRight: '8px'
                    }}>
                      {creationSuccess.code}
                    </code>
                    <IconButton
                      size="small"
                      onClick={() => {
                        navigator.clipboard.writeText(creationSuccess.code!);
                        alert("Code copied to clipboard!");
                      }}
                    >
                      <FileCopy fontSize="small" style={{ color: communityTheme.mainColor }} />
                    </IconButton>
                  </Box>
                </Box>
              )}
            </Box>
          ) : (
            <>
              <TextField
                margin="dense"
                label="Role"
                select
                fullWidth
                value={role}
                onChange={(e) => setRole(e.target.value)}
                InputLabelProps={{ style: { color: `${fgOnSecondary}CC` } }}
                InputProps={{ style: { color: fgOnSecondary } }}
              >
                <MenuItem value="member">Member</MenuItem>
                <MenuItem value="moderator">Moderator</MenuItem>
              </TextField>
              <TextField
                margin="dense"
                label="Expires In (days)"
                type="number"
                fullWidth
                value={expiryDays}
                onChange={(e) => setExpiryDays(parseInt(e.target.value) || 7)}
                InputLabelProps={{ style: { color: `${fgOnSecondary}CC` } }}
                InputProps={{ style: { color: fgOnSecondary } }}
              />

              {/* Channel Selection */}
              <Box mt={2}>
                <Typography variant="body2" style={{ color: fgOnSecondary, marginBottom: 8, fontWeight: 600 }}>
                  Channel Permissions
                </Typography>
                <TextField
                  placeholder="Search channels..."
                  fullWidth
                  value={channelSearchQuery}
                  onChange={(e) => setChannelSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: <Search style={{ marginRight: 8, color: `${fgOnSecondary}99` }} />,
                    style: { color: fgOnSecondary, fontSize: '0.9em' }
                  }}
                  style={{ marginBottom: 12 }}
                />
                <Box style={{ maxHeight: 200, overflow: 'auto', border: `1px solid ${fgOnSecondary}33`, borderRadius: 4, padding: 8 }}>
                  {getFilteredChannels.length === 0 ? (
                    <Typography variant="body2" style={{ color: `${fgOnSecondary}99`, padding: 8, textAlign: 'center' }}>
                      No channels found
                    </Typography>
                  ) : (
                    getFilteredChannels.map((ch: any) => {
                      const privacyType = ch.channelPrivacySettings?.type || 'public';
                      const isPrivate = privacyType !== 'public';
                      return (
                        <Box key={ch._id} display="flex" alignItems="center" mb={0.5}>
                          <Checkbox
                            checked={selectedChannelIds.includes(ch._id)}
                            onChange={() => handleToggleChannel(ch._id)}
                            style={{ color: communityTheme.mainColor, padding: '4px 8px' }}
                          />
                          <Typography variant="body2" style={{ color: fgOnSecondary, flex: 1 }}>
                            {ch.channelName}
                          </Typography>
                          {isPrivate && (
                            <Lock fontSize="small" style={{ color: `${fgOnSecondary}66`, marginLeft: 8 }} />
                          )}
                        </Box>
                      );
                    })
                  )}
                </Box>
                <Typography variant="caption" style={{ color: `${fgOnSecondary}99`, marginTop: 8, display: 'block' }}>
                  {selectedChannelIds.length === 0
                    ? 'No channels selected - members will have access to all public channels'
                    : `${selectedChannelIds.length} channel(s) selected`}
                </Typography>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          {creationSuccess?.type === 'public' ? (
            <>
              <Button
                onClick={() => {
                  setCreationSuccess(null);
                  setCloneSource(null);
                  // Reset form to defaults
                  setRole("member");
                  setExpiryDays(7);
                  setSelectedChannelIds([]);
                  setChannelSearchQuery("");
                }}
                variant="outlined"
                style={{ color: communityTheme.mainColor, borderColor: communityTheme.mainColor }}
              >
                Send Another
              </Button>
              <Button
                onClick={() => {
                  setCreatePublicDialog(false);
                  setCreationSuccess(null);
                  setCloneSource(null);
                }}
                variant="contained"
                style={{ backgroundColor: communityTheme.mainColor, color: communityTheme.secondaryColor }}
              >
                Close
              </Button>
            </>
          ) : (
            <>
              <Button
                onClick={() => {
                  setCreatePublicDialog(false);
                  setCloneSource(null);
                  setCreationSuccess(null);
                }}
                disabled={creating}
                style={{ color: fgOnSecondary }}
              >
                Cancel
              </Button>
              <Button
                color="primary"
                variant="contained"
                onClick={handleCreatePublic}
                disabled={creating}
                style={{ backgroundColor: communityTheme.mainColor, color: communityTheme.secondaryColor }}
              >
                {creating ? (cloneSource ? "Cloning…" : "Creating…") : (cloneSource ? "Clone" : "Create")}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Create Private Invitation Dialog */}
      <Dialog
        open={createPrivateDialog}
        onClose={() => {
          if (!creating) {
            setCreatePrivateDialog(false);
            setCreationSuccess(null);
          }
        }}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          style: {
            backgroundColor: communityTheme.secondaryColor,
            color: fgOnSecondary,
            fontFamily: communityTheme.fontStyle,
          },
        }}
      >
        <DialogTitle style={{ color: fgOnSecondary }}>
          {creationSuccess?.type === 'private' ? "✅ Invitation Sent Successfully!" : "Create Private Invitation"}
        </DialogTitle>
        <DialogContent>
          {creationSuccess?.type === 'private' ? (
            <Box py={2}>
              <Typography variant="body1" style={{ color: fgOnSecondary, marginBottom: 16 }}>
                Private invitation sent successfully!
              </Typography>
              <Typography variant="body2" style={{ color: `${fgOnSecondary}99` }}>
                The invitation email has been sent to the recipient.
              </Typography>
            </Box>
          ) : (
            <>
          <TextField
            autoFocus
            margin="dense"
            label="Email"
            type="email"
            fullWidth
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            InputLabelProps={{ style: { color: `${fgOnSecondary}CC` } }}
            InputProps={{ style: { color: fgOnSecondary } }}
          />
          <TextField
            margin="dense"
            label="First Name (optional)"
            fullWidth
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            InputLabelProps={{ style: { color: `${fgOnSecondary}CC` } }}
            InputProps={{ style: { color: fgOnSecondary } }}
          />
          <TextField
            margin="dense"
            label="Last Name (optional)"
            fullWidth
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            InputLabelProps={{ style: { color: `${fgOnSecondary}CC` } }}
            InputProps={{ style: { color: fgOnSecondary } }}
          />
          <TextField
            margin="dense"
            label="Phone (optional)"
            fullWidth
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            placeholder="(555) 555-5555"
            InputLabelProps={{ style: { color: `${fgOnSecondary}CC` } }}
            InputProps={{ style: { color: fgOnSecondary } }}
          />
          <TextField
            margin="dense"
            label="Role"
            select
            fullWidth
            value={role}
            onChange={(e) => setRole(e.target.value)}
            InputLabelProps={{ style: { color: `${fgOnSecondary}CC` } }}
            InputProps={{ style: { color: fgOnSecondary } }}
          >
            <MenuItem value="member">Member</MenuItem>
            <MenuItem value="moderator">Moderator</MenuItem>
            <MenuItem value="CommunityAdmin">Community Admin</MenuItem>
          </TextField>
          <TextField
            margin="dense"
            label="Expires In (days)"
            type="number"
            fullWidth
            value={expiryDays}
            onChange={(e) => setExpiryDays(parseInt(e.target.value) || 7)}
            InputLabelProps={{ style: { color: `${fgOnSecondary}CC` } }}
            InputProps={{ style: { color: fgOnSecondary } }}
          />

          {/* Channel Selection */}
          <Box mt={2}>
            <Typography variant="body2" style={{ color: fgOnSecondary, marginBottom: 8, fontWeight: 600 }}>
              Channel Permissions
            </Typography>
            <TextField
              placeholder="Search channels..."
              fullWidth
              value={channelSearchQuery}
              onChange={(e) => setChannelSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <Search style={{ marginRight: 8, color: `${fgOnSecondary}99` }} />,
                style: { color: fgOnSecondary, fontSize: '0.9em' }
              }}
              style={{ marginBottom: 12 }}
            />
            <Box style={{ maxHeight: 200, overflow: 'auto', border: `1px solid ${fgOnSecondary}33`, borderRadius: 4, padding: 8 }}>
              {getFilteredChannels.length === 0 ? (
                <Typography variant="body2" style={{ color: `${fgOnSecondary}99`, padding: 8, textAlign: 'center' }}>
                  No channels found
                </Typography>
              ) : (
                getFilteredChannels.map((ch: any) => {
                  const privacyType = ch.channelPrivacySettings?.type || 'public';
                  const isPrivate = privacyType !== 'public';
                  return (
                    <Box key={ch._id} display="flex" alignItems="center" mb={0.5}>
                      <Checkbox
                        checked={selectedChannelIds.includes(ch._id)}
                        onChange={() => handleToggleChannel(ch._id)}
                        style={{ color: communityTheme.mainColor, padding: '4px 8px' }}
                      />
                      <Typography variant="body2" style={{ color: fgOnSecondary, flex: 1 }}>
                        {ch.channelName}
                      </Typography>
                      {isPrivate && (
                        <Lock fontSize="small" style={{ color: `${fgOnSecondary}66`, marginLeft: 8 }} />
                      )}
                    </Box>
                  );
                })
              )}
            </Box>
            <Typography variant="caption" style={{ color: `${fgOnSecondary}99`, marginTop: 8, display: 'block' }}>
              {selectedChannelIds.length === 0
                ? 'No channels selected - members will have access to all public channels'
                : `${selectedChannelIds.length} channel(s) selected`}
            </Typography>
          </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          {creationSuccess?.type === 'private' ? (
            <>
              <Button
                onClick={() => {
                  setCreationSuccess(null);
                  // Reset form to defaults
                  setEmail("");
                  setFirstName("");
                  setLastName("");
                  setPhoneNumber("");
                  setRole("member");
                  setExpiryDays(7);
                  setSelectedChannelIds([]);
                  setChannelSearchQuery("");
                }}
                variant="outlined"
                style={{ color: communityTheme.mainColor, borderColor: communityTheme.mainColor }}
              >
                Send Another
              </Button>
              <Button
                onClick={() => {
                  setCreatePrivateDialog(false);
                  setCreationSuccess(null);
                  setEmail("");
                  setFirstName("");
                  setLastName("");
                  setPhoneNumber("");
                }}
                variant="contained"
                style={{ backgroundColor: communityTheme.mainColor, color: communityTheme.secondaryColor }}
              >
                Close
              </Button>
            </>
          ) : (
            <>
              <Button
                onClick={() => {
                  setCreatePrivateDialog(false);
                  setCreationSuccess(null);
                }}
                disabled={creating}
                style={{ color: fgOnSecondary }}
              >
                Cancel
              </Button>
              <Button
                color="primary"
                variant="contained"
                onClick={handleCreatePrivate}
                disabled={creating || !email}
                style={{ backgroundColor: communityTheme.mainColor, color: communityTheme.secondaryColor }}
              >
                {creating ? "Creating…" : "Create & Send"}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Pause Dialog */}
      <Dialog
        open={pauseDialog}
        onClose={() => setPauseDialog(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          style: {
            backgroundColor: communityTheme.secondaryColor,
            color: fgOnSecondary,
            fontFamily: communityTheme.fontStyle,
          },
        }}
      >
        <DialogTitle style={{ color: fgOnSecondary }}>Pause Public Invitation</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Message to show users"
            fullWidth
            multiline
            rows={3}
            value={pauseMessage}
            onChange={(e) => setPauseMessage(e.target.value)}
            placeholder="e.g., Registration temporarily closed"
            InputLabelProps={{ style: { color: `${fgOnSecondary}CC` } }}
            InputProps={{ style: { color: fgOnSecondary } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPauseDialog(false)} style={{ color: fgOnSecondary }}>
            Cancel
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={handlePause}
            style={{ backgroundColor: communityTheme.mainColor, color: communityTheme.secondaryColor }}
          >
            Pause
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Resend Confirmation Dialog */}
      <Dialog
        open={bulkResendDialog}
        onClose={() => !bulkResending && setBulkResendDialog(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          style: {
            backgroundColor: communityTheme.secondaryColor,
            color: fgOnSecondary,
            fontFamily: communityTheme.fontStyle,
            borderRadius: "12px",
          },
        }}
      >
        <Box style={{ padding: "24px 24px 20px 24px" }}>
          <Box display="flex" alignItems="center" mb={2}>
            <Box
              style={{
                backgroundColor: `${communityTheme.mainColor}22`,
                borderRadius: "50%",
                padding: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginRight: "16px",
              }}
            >
              <Send style={{ fontSize: "2em", color: communityTheme.mainColor }} />
            </Box>
            <Typography variant="h6" style={{ color: fgOnSecondary, fontWeight: 600 }}>
              Resend Invitations
            </Typography>
          </Box>

          <Typography variant="body1" style={{ color: fgOnSecondary, padding: "8px 0 16px 0", lineHeight: 1.6 }}>
            Do you want to resend invitations to <strong>{selected.length}</strong> member{selected.length !== 1 ? 's' : ''}?
          </Typography>

          <Box
            style={{
              backgroundColor: `${communityTheme.mainColor}11`,
              borderLeft: `4px solid ${communityTheme.mainColor}`,
              padding: "12px 16px",
              borderRadius: "4px",
              marginBottom: "16px",
            }}
          >
            <Typography variant="body2" style={{ color: `${fgOnSecondary}DD`, fontSize: "0.9em" }}>
              This will send invitation emails to all selected members.
            </Typography>
          </Box>

          <Box display="flex" justifyContent="flex-end" style={{ gap: 12 }}>
            <Button
              onClick={() => setBulkResendDialog(false)}
              disabled={bulkResending}
              variant="outlined"
              style={{
                color: fgOnSecondary,
                borderColor: `${fgOnSecondary}33`,
                textTransform: "none",
                padding: "8px 24px",
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleBulkResend}
              disabled={bulkResending}
              style={{
                backgroundColor: communityTheme.mainColor,
                color: communityTheme.secondaryColor,
                textTransform: "none",
                padding: "8px 32px",
                boxShadow: `0 4px 12px ${communityTheme.mainColor}44`,
              }}
            >
              {bulkResending ? (
                <Box display="flex" alignItems="center">
                  <CircularProgress size={20} style={{ color: communityTheme.secondaryColor, marginRight: 8 }} />
                  Sending...
                </Box>
              ) : (
                "Resend Invitations"
              )}
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* Edit Invitation Dialog */}
      <Dialog
        open={editDialog}
        onClose={() => !updating && setEditDialog(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          style: {
            backgroundColor: communityTheme.secondaryColor,
            color: fgOnSecondary,
            fontFamily: communityTheme.fontStyle,
          },
        }}
      >
        <DialogTitle style={{ color: fgOnSecondary }}>
          Edit Invitation Permissions
        </DialogTitle>
        <DialogContent>
          <Box py={1}>
            <Typography variant="body2" style={{ color: `${fgOnSecondary}99`, marginBottom: 16 }}>
              {editingInvitation?.invitationType === "public"
                ? "Changes will apply to all future acceptances of this invitation."
                : `Editing invitation for ${editingInvitation?.email}`}
            </Typography>

            <TextField
              margin="dense"
              label="Role"
              select
              fullWidth
              value={editRole}
              onChange={(e) => setEditRole(e.target.value)}
              InputLabelProps={{ style: { color: `${fgOnSecondary}CC` } }}
              InputProps={{ style: { color: fgOnSecondary } }}
            >
              <MenuItem value="member">Member</MenuItem>
              <MenuItem value="moderator">Moderator</MenuItem>
              <MenuItem value="CommunityAdmin">Community Admin</MenuItem>
            </TextField>

            <TextField
              margin="dense"
              label="Expires In (days)"
              type="number"
              fullWidth
              value={editExpiryDays}
              onChange={(e) => setEditExpiryDays(parseInt(e.target.value) || 7)}
              InputLabelProps={{ style: { color: `${fgOnSecondary}CC` } }}
              InputProps={{ style: { color: fgOnSecondary } }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setEditDialog(false);
              setEditingInvitation(null);
            }}
            disabled={updating}
            style={{ color: fgOnSecondary }}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={handleUpdateInvitation}
            disabled={updating}
            style={{ backgroundColor: communityTheme.mainColor, color: communityTheme.secondaryColor }}
          >
            {updating ? "Updating..." : "Update"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Channel Access Popover */}
      <Popover
        open={Boolean(channelPopover)}
        anchorEl={channelPopover?.el}
        onClose={() => setChannelPopover(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        PaperProps={{
          style: {
            backgroundColor: communityTheme.secondaryColor,
            color: fgOnSecondary,
            padding: '16px',
            maxWidth: '400px',
            border: `1px solid ${communityTheme.mainColor}33`,
          },
        }}
      >
        {channelPopover?.loading ? (
          <Box display="flex" justifyContent="center" p={2}>
            <CircularProgress size={24} style={{ color: communityTheme.mainColor }} />
          </Box>
        ) : channelPopover?.data ? (
          <Box>
            <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: 8, color: fgOnSecondary }}>
              Channel Access
            </Typography>
            <Box mb={2}>
              <Typography variant="body2" style={{ color: `${fgOnSecondary}99` }}>
                <strong>Public:</strong> {channelPopover.data.publicChannels} |{' '}
                <strong>Private:</strong> {channelPopover.data.privateChannels} |{' '}
                <strong>Restricted:</strong> {channelPopover.data.restrictedChannels}
              </Typography>
            </Box>
            {channelPopover.data.channels && channelPopover.data.channels.length > 0 ? (
              <Box>
                <Typography variant="body2" style={{ fontWeight: 'bold', marginBottom: 4, color: fgOnSecondary }}>
                  Channels:
                </Typography>
                {channelPopover.data.channels.map((ch: any, idx: number) => (
                  <Box key={idx} display="flex" alignItems="center" mb={0.5}>
                    <Chip
                      label={ch.privacyType}
                      size="small"
                      style={{
                        marginRight: 8,
                        fontSize: '0.7em',
                        height: '20px',
                        backgroundColor:
                          ch.privacyType === 'public'
                            ? '#4caf5022'
                            : ch.privacyType === 'private'
                            ? '#2196f322'
                            : '#ff980022',
                        color:
                          ch.privacyType === 'public'
                            ? '#4caf50'
                            : ch.privacyType === 'private'
                            ? '#2196f3'
                            : '#ff9800',
                      }}
                    />
                    <Typography variant="body2" style={{ color: fgOnSecondary, fontSize: '0.9em' }}>
                      {ch.channelName}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" style={{ color: `${fgOnSecondary}99`, fontStyle: 'italic' }}>
                No specific channels assigned (full community access)
              </Typography>
            )}
          </Box>
        ) : (
          <Typography variant="body2" style={{ color: `${fgOnSecondary}99` }}>
            Failed to load channel access information
          </Typography>
        )}
      </Popover>

      {/* Actions Menu (for mobile) */}
      <Menu
        anchorEl={menuAnchor?.el}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        PaperProps={{
          style: {
            backgroundColor: communityTheme.secondaryColor,
            color: fgOnSecondary,
          },
        }}
      >
        {menuAnchor?.inv && (
          <>
            {/* Show expires date */}
            <MenuItem disabled style={{ color: `${fgOnSecondary}99`, fontSize: "0.9em" }}>
              Expires: {new Date(menuAnchor.inv.expiresAt).toLocaleDateString()}
            </MenuItem>

            {/* Show usage/resend count */}
            <MenuItem disabled style={{ color: `${fgOnSecondary}99`, fontSize: "0.9em" }}>
              {currentTab === 0 ? `Usage: ${menuAnchor.inv.usageCount}` : `Resent: ${menuAnchor.inv.sentCount || 0} times`}
            </MenuItem>

            <Box style={{ borderTop: `1px solid ${fgOnSecondary}22`, margin: "4px 0" }} />

            {/* Actions */}
            {/* Edit action for active public or pending private */}
            {((currentTab === 0 && menuAnchor.inv.status === "active") || (currentTab === 1 && menuAnchor.inv.status === "pending")) && (
              <MenuItem
                onClick={() => {
                  handleEditInvitation(menuAnchor.inv);
                  setMenuAnchor(null);
                }}
              >
                <Edit fontSize="small" style={{ marginRight: 8, color: communityTheme.mainColor }} />
                Edit Permissions
              </MenuItem>
            )}
            {currentTab === 0 && (
              <MenuItem
                onClick={() => {
                  handleClone(menuAnchor.inv);
                  setMenuAnchor(null);
                }}
              >
                <FileCopy fontSize="small" style={{ marginRight: 8, color: communityTheme.mainColor }} />
                Clone
              </MenuItem>
            )}
            {currentTab === 0 && menuAnchor.inv.status === "active" && (
              <>
                <MenuItem
                  onClick={() => {
                    setSelectedInvitation(menuAnchor.inv);
                    setPauseDialog(true);
                    setMenuAnchor(null);
                  }}
                >
                  <Pause fontSize="small" style={{ marginRight: 8, color: communityTheme.mainColor }} />
                  Pause
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    handleCancel(menuAnchor.inv);
                    setMenuAnchor(null);
                  }}
                  style={{ color: "#f44336" }}
                >
                  <Cancel fontSize="small" style={{ marginRight: 8 }} />
                  Cancel
                </MenuItem>
              </>
            )}
            {currentTab === 0 && menuAnchor.inv.status === "paused" && (
              <MenuItem
                onClick={() => {
                  handleResume(menuAnchor.inv);
                  setMenuAnchor(null);
                }}
              >
                <PlayArrow fontSize="small" style={{ marginRight: 8, color: communityTheme.mainColor }} />
                Resume
              </MenuItem>
            )}
            {currentTab === 1 && menuAnchor.inv.status === "pending" && (
              <>
                <MenuItem
                  onClick={() => {
                    handleResend(menuAnchor.inv);
                    setMenuAnchor(null);
                  }}
                >
                  <Send fontSize="small" style={{ marginRight: 8, color: communityTheme.mainColor }} />
                  Resend
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    handleRevoke(menuAnchor.inv);
                    setMenuAnchor(null);
                  }}
                  style={{ color: "#f44336" }}
                >
                  <Delete fontSize="small" style={{ marginRight: 8 }} />
                  Revoke
                </MenuItem>
              </>
            )}
          </>
        )}
      </Menu>

      <MuiCard
        className={classes.card}
        style={{
          minHeight: "95vh",
          width: "100%",
          maxWidth: "1600px",
          display: "flex",
          flexDirection: "column",
          backgroundColor: communityTheme.secondaryColor,
          color: fgOnSecondary,
          border: `1px solid ${fgOnSecondary}33`,
          boxShadow: `0 8px 24px ${fgOnSecondary}22`,
          fontFamily: communityTheme.fontStyle,
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <Box
          className={classes.header}
          style={{
            flexShrink: 0,
            backgroundColor: communityTheme.mainColor,
            color: communityTheme.secondaryColor,
          }}
        >
          <IconButton onClick={() => history.push("/user/dashboard")}>
            <img src={backIcon} alt="Back" className={classes.backIcon} />
          </IconButton>
          <Box className={classes.logoWrapper}>
            <img src={appLogo} alt="Logo" className={classes.logo} />
          </Box>
        </Box>

        {/* Content */}
        <Box style={{ flexShrink: 0, padding: "16px 16px 0 16px" }}>
          <Typography
            className={`${classes.titleCon} ${classes.onboardingTitle}`}
            component="h1"
            style={{ color: communityTheme.mainColor }}
          >
            Manage Invitations
          </Typography>

          <Typography
            className={`${classes.titleCon} ${classes.onboardingHeading}`}
            component="h2"
            style={{ color: fgOnSecondary, marginBottom: 16 }}
          >
            Community: {selectedCommunity}
          </Typography>

          {/* Type Filter Buttons - Public vs Private */}
          <Box display="flex" justifyContent="center" mb={3}>
            <ButtonGroup size="large" style={{ boxShadow: `0 4px 12px ${fgOnSecondary}22` }}>
              <Button
                onClick={() => setCurrentTab(0)}
                style={{
                  backgroundColor: currentTab === 0 ? communityTheme.mainColor : communityTheme.secondaryColor,
                  color: currentTab === 0 ? communityTheme.secondaryColor : fgOnSecondary,
                  fontWeight: currentTab === 0 ? 700 : 500,
                  padding: "12px 32px",
                  textTransform: "none",
                  fontSize: "1.1em",
                  border: `2px solid ${currentTab === 0 ? communityTheme.mainColor : `${fgOnSecondary}33`}`,
                  transform: currentTab === 0 ? "scale(0.98)" : "scale(1)",
                  boxShadow: currentTab === 0 ? "inset 0 3px 8px rgba(0,0,0,0.2)" : "none",
                  transition: "all 0.2s ease",
                }}
              >
                Public ({publicInvitations.length})
              </Button>
              <Button
                onClick={() => setCurrentTab(1)}
                style={{
                  backgroundColor: currentTab === 1 ? communityTheme.mainColor : communityTheme.secondaryColor,
                  color: currentTab === 1 ? communityTheme.secondaryColor : fgOnSecondary,
                  fontWeight: currentTab === 1 ? 700 : 500,
                  padding: "12px 32px",
                  textTransform: "none",
                  fontSize: "1.1em",
                  border: `2px solid ${currentTab === 1 ? communityTheme.mainColor : `${fgOnSecondary}33`}`,
                  transform: currentTab === 1 ? "scale(0.98)" : "scale(1)",
                  boxShadow: currentTab === 1 ? "inset 0 3px 8px rgba(0,0,0,0.2)" : "none",
                  transition: "all 0.2s ease",
                }}
              >
                Private ({privateInvitations.length})
              </Button>
            </ButtonGroup>
          </Box>

          {/* Action Bar - Search and New Button */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <div
              className={classes.searchInput}
              style={{
                border: `1px solid ${fgOnSecondary}33`,
                backgroundColor: `${fgOnSecondary}0D`,
                flex: 1,
                marginRight: 16,
              }}
            >
              <InputBase
                placeholder="Search invitations..."
                fullWidth
                startAdornment={<Search style={{ marginRight: 8, color: `${fgOnSecondary}99` }} />}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                inputProps={{ style: { color: fgOnSecondary } }}
              />
            </div>
            <Box display="flex" style={{ gap: 12 }}>
              {selected.length > 0 && currentTab === 1 && (
                <Button
                  variant="contained"
                  startIcon={<Send />}
                  onClick={() => setBulkResendDialog(true)}
                  style={{
                    backgroundColor: communityTheme.mainColor,
                    color: communityTheme.secondaryColor,
                    textTransform: "none",
                    padding: "10px 24px",
                  }}
                >
                  Resend to {selected.length} Selected
                </Button>
              )}
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => currentTab === 0 ? setCreatePublicDialog(true) : setCreatePrivateDialog(true)}
                style={{
                  backgroundColor: communityTheme.mainColor,
                  color: communityTheme.secondaryColor,
                  textTransform: "none",
                  padding: "10px 24px",
                }}
              >
                {currentTab === 0 ? "New Public" : "New Private"}
              </Button>
            </Box>
          </Box>

          {/* Filter Buttons - Status and Role */}
          <Box display="flex" alignItems="center" mb={3} flexWrap="wrap" style={{ gap: 12 }}>
            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
              <Typography variant="body2" style={{ color: `${fgOnSecondary}99`, fontWeight: 600 }}>
                Status:
              </Typography>
              <ButtonGroup size="small">
                <Button
                  onClick={() => setStatusFilter("all")}
                  style={{
                    backgroundColor: statusFilter === "all" ? communityTheme.mainColor : `${fgOnSecondary}11`,
                    color: statusFilter === "all" ? communityTheme.secondaryColor : fgOnSecondary,
                    fontWeight: statusFilter === "all" ? 600 : 400,
                    textTransform: "none",
                    transform: statusFilter === "all" ? "scale(0.95)" : "scale(1)",
                    boxShadow: statusFilter === "all" ? "inset 0 2px 4px rgba(0,0,0,0.2)" : "none",
                  }}
                >
                  All
                </Button>
                <Button
                  onClick={() => setStatusFilter("pending")}
                  style={{
                    backgroundColor: statusFilter === "pending" ? communityTheme.mainColor : `${fgOnSecondary}11`,
                    color: statusFilter === "pending" ? communityTheme.secondaryColor : fgOnSecondary,
                    fontWeight: statusFilter === "pending" ? 600 : 400,
                    textTransform: "none",
                    transform: statusFilter === "pending" ? "scale(0.95)" : "scale(1)",
                    boxShadow: statusFilter === "pending" ? "inset 0 2px 4px rgba(0,0,0,0.2)" : "none",
                  }}
                >
                  Pending
                </Button>
                <Button
                  onClick={() => setStatusFilter("accepted")}
                  style={{
                    backgroundColor: statusFilter === "accepted" ? communityTheme.mainColor : `${fgOnSecondary}11`,
                    color: statusFilter === "accepted" ? communityTheme.secondaryColor : fgOnSecondary,
                    fontWeight: statusFilter === "accepted" ? 600 : 400,
                    textTransform: "none",
                    transform: statusFilter === "accepted" ? "scale(0.95)" : "scale(1)",
                    boxShadow: statusFilter === "accepted" ? "inset 0 2px 4px rgba(0,0,0,0.2)" : "none",
                  }}
                >
                  Accepted
                </Button>
                <Button
                  onClick={() => setStatusFilter("revoked")}
                  style={{
                    backgroundColor: statusFilter === "revoked" ? communityTheme.mainColor : `${fgOnSecondary}11`,
                    color: statusFilter === "revoked" ? communityTheme.secondaryColor : fgOnSecondary,
                    fontWeight: statusFilter === "revoked" ? 600 : 400,
                    textTransform: "none",
                    transform: statusFilter === "revoked" ? "scale(0.95)" : "scale(1)",
                    boxShadow: statusFilter === "revoked" ? "inset 0 2px 4px rgba(0,0,0,0.2)" : "none",
                  }}
                >
                  Revoked
                </Button>
              </ButtonGroup>
            </Box>

            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
              <Typography variant="body2" style={{ color: `${fgOnSecondary}99`, fontWeight: 600 }}>
                Role:
              </Typography>
              <ButtonGroup size="small">
                <Button
                  onClick={() => setRoleFilter("all")}
                  style={{
                    backgroundColor: roleFilter === "all" ? communityTheme.mainColor : `${fgOnSecondary}11`,
                    color: roleFilter === "all" ? communityTheme.secondaryColor : fgOnSecondary,
                    fontWeight: roleFilter === "all" ? 600 : 400,
                    textTransform: "none",
                    transform: roleFilter === "all" ? "scale(0.95)" : "scale(1)",
                    boxShadow: roleFilter === "all" ? "inset 0 2px 4px rgba(0,0,0,0.2)" : "none",
                  }}
                >
                  All
                </Button>
                <Button
                  onClick={() => setRoleFilter("member")}
                  style={{
                    backgroundColor: roleFilter === "member" ? communityTheme.mainColor : `${fgOnSecondary}11`,
                    color: roleFilter === "member" ? communityTheme.secondaryColor : fgOnSecondary,
                    fontWeight: roleFilter === "member" ? 600 : 400,
                    textTransform: "none",
                    transform: roleFilter === "member" ? "scale(0.95)" : "scale(1)",
                    boxShadow: roleFilter === "member" ? "inset 0 2px 4px rgba(0,0,0,0.2)" : "none",
                  }}
                >
                  Member
                </Button>
                <Button
                  onClick={() => setRoleFilter("moderator")}
                  style={{
                    backgroundColor: roleFilter === "moderator" ? communityTheme.mainColor : `${fgOnSecondary}11`,
                    color: roleFilter === "moderator" ? communityTheme.secondaryColor : fgOnSecondary,
                    fontWeight: roleFilter === "moderator" ? 600 : 400,
                    textTransform: "none",
                    transform: roleFilter === "moderator" ? "scale(0.95)" : "scale(1)",
                    boxShadow: roleFilter === "moderator" ? "inset 0 2px 4px rgba(0,0,0,0.2)" : "none",
                  }}
                >
                  Moderator
                </Button>
                <Button
                  onClick={() => setRoleFilter("CommunityAdmin")}
                  style={{
                    backgroundColor: roleFilter === "CommunityAdmin" ? communityTheme.mainColor : `${fgOnSecondary}11`,
                    color: roleFilter === "CommunityAdmin" ? communityTheme.secondaryColor : fgOnSecondary,
                    fontWeight: roleFilter === "CommunityAdmin" ? 600 : 400,
                    textTransform: "none",
                    transform: roleFilter === "CommunityAdmin" ? "scale(0.95)" : "scale(1)",
                    boxShadow: roleFilter === "CommunityAdmin" ? "inset 0 2px 4px rgba(0,0,0,0.2)" : "none",
                  }}
                >
                  Admin
                </Button>
              </ButtonGroup>
            </Box>
          </Box>

          {/* Description Text */}
          <Typography
            variant="body2"
            style={{
              color: `${fgOnSecondary}99`,
              marginBottom: 16,
              fontStyle: "italic",
              paddingLeft: 8,
            }}
          >
            {currentTab === 0
              ? "Public invitations can be shared with anyone and are non-restrictive."
              : "Private invitations are for direct invitations to private channels, or for moderators and admins."}
          </Typography>
        </Box>

        {/* Table */}
        <Box style={{ flex: 1, overflow: "auto", padding: "0 16px 16px 16px" }}>
          {loading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : paginatedInvitations.length === 0 ? (
            <Box p={4} textAlign="center">
              <Typography variant="body1" style={{ color: `${fgOnSecondary}99` }}>
                No invitations found
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {/* Checkbox column for private pending invitations */}
                      {currentTab === 1 && paginatedInvitations.some((inv) => inv.status === "pending") && (
                        <TableCell padding="checkbox">
                          <Checkbox
                            indeterminate={
                              selected.length > 0 &&
                              selected.length < paginatedInvitations.filter((inv) => inv.status === "pending").length
                            }
                            checked={
                              paginatedInvitations.filter((inv) => inv.status === "pending").length > 0 &&
                              selected.length === paginatedInvitations.filter((inv) => inv.status === "pending").length
                            }
                            onChange={handleSelectAll}
                            style={{ color: communityTheme.mainColor }}
                          />
                        </TableCell>
                      )}
                      {/* Show Code only for public invitations */}
                      {currentTab === 0 && (
                        <TableCell style={{ color: fgOnSecondary, fontWeight: "bold" }}>Code</TableCell>
                      )}
                      {/* For private invitations, show Email first */}
                      {currentTab === 1 && (
                        <TableCell style={{ color: fgOnSecondary, fontWeight: "bold" }}>
                          <TableSortLabel
                            active={sortBy === "email"}
                            direction={sortBy === "email" ? sortOrder : "asc"}
                            onClick={() => handleSort("email")}
                            style={{ color: fgOnSecondary }}
                          >
                            Email
                          </TableSortLabel>
                        </TableCell>
                      )}
                      {currentTab === 1 && (
                        <TableCell style={{ color: fgOnSecondary, fontWeight: "bold" }}>Name</TableCell>
                      )}
                      {currentTab === 1 && (
                        <TableCell style={{ color: fgOnSecondary, fontWeight: "bold" }}>
                          <TableSortLabel
                            active={sortBy === "sentAt"}
                            direction={sortBy === "sentAt" ? sortOrder : "asc"}
                            onClick={() => handleSort("sentAt")}
                            style={{ color: fgOnSecondary }}
                          >
                            Last Sent
                          </TableSortLabel>
                        </TableCell>
                      )}
                      <TableCell style={{ color: fgOnSecondary, fontWeight: "bold" }}>
                        {currentTab === 1 ? (
                          <TableSortLabel
                            active={sortBy === "status"}
                            direction={sortBy === "status" ? sortOrder : "asc"}
                            onClick={() => handleSort("status")}
                            style={{ color: fgOnSecondary }}
                          >
                            Status
                          </TableSortLabel>
                        ) : (
                          "Status"
                        )}
                      </TableCell>
                      <TableCell style={{ color: fgOnSecondary, fontWeight: "bold" }}>
                        {currentTab === 1 ? (
                          <TableSortLabel
                            active={sortBy === "role"}
                            direction={sortBy === "role" ? sortOrder : "asc"}
                            onClick={() => handleSort("role")}
                            style={{ color: fgOnSecondary }}
                          >
                            Role
                          </TableSortLabel>
                        ) : (
                          "Role"
                        )}
                      </TableCell>
                      <TableCell style={{ color: fgOnSecondary, fontWeight: "bold" }}>
                        Channels
                      </TableCell>
                      {!isMobile && (
                        <TableCell style={{ color: fgOnSecondary, fontWeight: "bold" }}>
                          {currentTab === 0 ? "Usage" : "Resend Count"}
                        </TableCell>
                      )}
                      {!isMobile && (
                        <TableCell style={{ color: fgOnSecondary, fontWeight: "bold" }}>
                          Expires
                        </TableCell>
                      )}
                      {!isMobile && (
                        <TableCell style={{ color: fgOnSecondary, fontWeight: "bold" }}>
                          Actions
                        </TableCell>
                      )}
                      {isMobile && (
                        <TableCell style={{ color: fgOnSecondary, fontWeight: "bold" }}>
                        </TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedInvitations.map((inv) => {
                    const statusStyle = getStatusColor(inv.status);
                    const lastSent = inv.sentHistory?.[inv.sentHistory.length - 1];
                    const sentHistoryTooltip = inv.sentHistory
                      ?.map((h, idx) => `${idx + 1}. ${new Date(h.sentAt).toLocaleString()}`)
                      .join("\n") || "No history";

                    return (
                      <TableRow key={inv._id}>
                        {/* Checkbox column - only for private pending invitations */}
                        {currentTab === 1 && paginatedInvitations.some((i) => i.status === "pending") && (
                          <TableCell padding="checkbox">
                            {inv.status === "pending" ? (
                              <Checkbox
                                checked={selected.includes(inv._id)}
                                onChange={() => handleSelectOne(inv._id)}
                                style={{ color: communityTheme.mainColor }}
                              />
                            ) : (
                              <Box style={{ width: 42 }} />
                            )}
                          </TableCell>
                        )}
                        {/* Code column - only for public */}
                        {currentTab === 0 && (
                          <TableCell style={{ color: fgOnSecondary }}>
                            <Box display="flex" alignItems="center">
                              <code style={{ marginRight: 8 }}>{inv.invitationCode}</code>
                              <IconButton size="small" onClick={() => handleCopyCode(inv.invitationCode)}>
                                <FileCopy fontSize="small" style={{ color: communityTheme.mainColor }} />
                              </IconButton>
                            </Box>
                          </TableCell>
                        )}

                        {/* Email column - only for private */}
                        {currentTab === 1 && (
                          <TableCell style={{ color: fgOnSecondary, fontWeight: 500 }}>{inv.email}</TableCell>
                        )}

                        {/* Name column - only for private */}
                        {currentTab === 1 && (
                          <TableCell style={{ color: fgOnSecondary }}>{inv.inviteeName || "-"}</TableCell>
                        )}

                        {/* Last Sent column - only for private */}
                        {currentTab === 1 && (
                          <TableCell style={{ color: fgOnSecondary }}>
                            {lastSent ? (
                              <Tooltip title={new Date(lastSent.sentAt).toLocaleString()}>
                                <span style={{ cursor: "help" }}>{moment(lastSent.sentAt).fromNow()}</span>
                              </Tooltip>
                            ) : "Not sent"}
                          </TableCell>
                        )}

                        {/* Status column */}
                        <TableCell>
                          <Chip
                            label={inv.status}
                            size="small"
                            style={{ backgroundColor: statusStyle.bg, color: statusStyle.color }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={getRoleDisplay(inv.role)}
                            size="small"
                            style={{
                              backgroundColor: `${communityTheme.mainColor}22`,
                              color: communityTheme.mainColor,
                              fontWeight: 600
                            }}
                          />
                        </TableCell>

                        {/* Channels column */}
                        <TableCell>
                          {inv.channelIds && inv.channelIds.length > 0 ? (
                            <Badge
                              badgeContent={inv.channelIds.length}
                              color="primary"
                              style={{ cursor: 'pointer' }}
                              onClick={(e) => handleChannelBadgeClick(e, inv._id)}
                            >
                              <Chip
                                label="View"
                                size="small"
                                style={{
                                  backgroundColor: `${communityTheme.mainColor}11`,
                                  color: communityTheme.mainColor,
                                  cursor: 'pointer'
                                }}
                                onClick={(e) => handleChannelBadgeClick(e, inv._id)}
                              />
                            </Badge>
                          ) : (
                            <Typography variant="body2" style={{ color: `${fgOnSecondary}66`, fontSize: '0.85em', fontStyle: 'italic' }}>
                              All
                            </Typography>
                          )}
                        </TableCell>

                        {/* Desktop columns - Usage/Resend Count */}
                        {!isMobile && (
                          <TableCell style={{ color: fgOnSecondary }}>
                            {currentTab === 0 ? (
                              inv.usageCount
                            ) : (
                              <Tooltip
                                title={
                                  <div style={{ whiteSpace: "pre-line", fontSize: "12px" }}>
                                    {sentHistoryTooltip}
                                  </div>
                                }
                                arrow
                              >
                                <span style={{ cursor: "help", borderBottom: `1px dotted ${fgOnSecondary}` }}>
                                  {inv.sentCount || 0}
                                </span>
                              </Tooltip>
                            )}
                          </TableCell>
                        )}

                        {/* Desktop columns - Expires */}
                        {!isMobile && (
                          <TableCell style={{ color: fgOnSecondary }}>
                            {new Date(inv.expiresAt).toLocaleDateString()}
                          </TableCell>
                        )}

                        {/* Desktop columns - Actions */}
                        {!isMobile && (
                          <TableCell>
                            <IconButton
                              size="small"
                              onClick={(e) => setMenuAnchor({ el: e.currentTarget, inv })}
                              title="Actions"
                            >
                              <MoreVert fontSize="small" style={{ color: communityTheme.mainColor }} />
                            </IconButton>
                          </TableCell>
                        )}

                        {/* Mobile menu button */}
                        {isMobile && (
                          <TableCell>
                            <IconButton
                              size="small"
                              onClick={(e) => setMenuAnchor({ el: e.currentTarget, inv })}
                            >
                              <MoreVert fontSize="small" style={{ color: communityTheme.mainColor }} />
                            </IconButton>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

              {/* Pagination controls - only for private invitations */}
              {currentTab === 1 && totalPages > 1 && (
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mt={2}
                  px={2}
                  style={{ borderTop: `1px solid ${fgOnSecondary}22`, paddingTop: 16 }}
                >
                  <Typography variant="body2" style={{ color: `${fgOnSecondary}99` }}>
                    Showing {currentPage * pageSize + 1}-
                    {Math.min((currentPage + 1) * pageSize, sortedInvitations.length)} of{" "}
                    {sortedInvitations.length} invitations
                  </Typography>
                  <Box display="flex">
                    <Button
                      size="small"
                      onClick={() => setCurrentPage(0)}
                      disabled={currentPage === 0}
                      style={{ color: fgOnSecondary, minWidth: 40, marginRight: 8 }}
                    >
                      {"<<"}
                    </Button>
                    <Button
                      size="small"
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage === 0}
                      style={{ color: fgOnSecondary, marginRight: 8 }}
                    >
                      Previous
                    </Button>
                    <Typography
                      variant="body2"
                      style={{
                        color: fgOnSecondary,
                        display: "flex",
                        alignItems: "center",
                        padding: "0 16px",
                      }}
                    >
                      Page {currentPage + 1} of {totalPages}
                    </Typography>
                    <Button
                      size="small"
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage === totalPages - 1}
                      style={{ color: fgOnSecondary, marginRight: 8 }}
                    >
                      Next
                    </Button>
                    <Button
                      size="small"
                      onClick={() => setCurrentPage(totalPages - 1)}
                      disabled={currentPage === totalPages - 1}
                      style={{ color: fgOnSecondary, minWidth: 40 }}
                    >
                      {">>"}
                    </Button>
                  </Box>
                </Box>
              )}
            </>
          )}
        </Box>
      </MuiCard>
    </div>
  );
}
